import type { FC, ReactElement } from "react"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import isNumber from "lodash-es/isNumber"

import {
    BetaMark,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { EFeedbackModalType, EFeedbackModalVariant, TFeedbackModalProps } from "@/3514/modals"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import classes from "./coach.group.styles"
import uiConfig from "./coach.group.ui.config"

type TProps = { type: "summary" | "individuals"; summaryId: number | undefined | null }
const CoachGroupSectionHeading: FC<TProps> = ({ type, summaryId }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const isSummaryIdPresent: boolean = isNumber(summaryId)

    const isReducedMotion: boolean = useReducedMotion()

    const handleBetaAction: TEmptyCallback = isSummaryIdPresent
        ? (): void =>
              modalDispatcher.showModal<TFeedbackModalProps>(EModalId.Feedback, {
                  type: EFeedbackModalType.General,
                  variant: eq(type, "summary")
                      ? EFeedbackModalVariant.AiGroupSummary
                      : EFeedbackModalVariant.AiIndividualSummary,
                  payload: { summaryId }
              })
        : emptyCallback

    return (
        <div className={classes.sectionHeadingContainer()}>
            <Typography
                tag={ETypographyTag.Heading2}
                size={ETypographySize.Medium}
                weight={ETypographyWeight.Medium}
                family={ETypographyFamily.DmSerif}
                color={ETypographyColor.Blue200}
                text={t(uiConfig.pageBlocksTitlesMap[type])}
            />

            <AnimatePresence initial={false}>
                {isSummaryIdPresent && eq(type, "summary") && (
                    <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: isReducedMotion ? 0 : 0.2, ease: "easeIn" }}
                    >
                        <BetaMark handleAction={handleBetaAction} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

CoachGroupSectionHeading.displayName = "CoachGroupSectionHeading"

export default CoachGroupSectionHeading
